const TAP = `
vec3 tap(sampler2D t, vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return textureGrad(t, mix(vec2(0.006), vec2(0.994), m), dFdx(uv), dFdy(uv)).rgb;
}
vec3 flowTap(sampler2D t, vec2 uv){
  if (uLite > 0.5) return tap(t, uv);
  vec2 f = vec2(sin(uv.y * 5.0 + uTime * 0.55 + sin(uv.x * 3.7)), cos(uv.x * 4.3 - uTime * 0.45 + sin(uv.y * 2.9)));
  float p0 = fract(uTime * 0.16);
  float p1 = fract(uTime * 0.16 + 0.5);
  float w0 = 1.0 - abs(1.0 - 2.0 * p0);
  return mix(tap(t, uv + f * (p1 - 0.5) * 0.055), tap(t, uv + f * (p0 - 0.5) * 0.055), w0);
}`;

const SEC = `
vec3 sec(sampler2D a, sampler2D b, float aa, float ab, vec2 p, float th, vec3 tint, float seed){
  vec3 c = flowTap(a, p / vec2(th * aa, th) + 0.5 + vec2(uTime * 0.018, 0.0));
  if (uLite < 0.5) {
    vec3 cb = flowTap(b, p / vec2(th * ab, th) * 0.8 + vec2(0.5 + seed, 0.25) - vec2(uTime * 0.014, 0.0));
    float m = smoothstep(-0.35, 0.35, sin(p.x * 0.35 + uTime * 0.2 + seed * 6.0) * cos(p.y * 0.5 - uTime * 0.17));
    c = mix(c, cb, m * 0.75);
  }
  return mix(c, tint, 0.2);
}`;

export const hallVert = `
varying vec3 vPos;
varying float vAlong;
varying vec2 vUv;
varying vec3 vN;
void main(){
  vUv = uv;
  vec4 w = modelMatrix * vec4(position, 1.0);
  vec3 n = normalize(mat3(modelMatrix) * normal);
  vN = n;
  vPos = w.xyz;
  vAlong = dot(w.xz, vec2(n.z, -n.x));
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

const ZONES = [0, 1, 2, 3, 4];
const ZONE_FN = `
float sOf(vec2 p){
  float d0 = p.y + uD * 0.5, d1 = uW * 0.5 - p.x, d2 = uD * 0.5 - p.y, d3 = p.x + uW * 0.5;
  float m = min(min(d0, d1), min(d2, d3));
  if (m == d0) return p.x + uW * 0.5;
  if (m == d1) return uW + p.y + uD * 0.5;
  if (m == d2) return uW + uD + uW * 0.5 - p.x;
  return 2.0 * uW + uD + uD * 0.5 - p.y;
}
float outside(float s, vec2 r){
  float c = 0.5 * (r.x + r.y), h = 0.5 * (r.y - r.x);
  float d = abs(mod(s - c + uP * 0.5, uP) - uP * 0.5);
  return max(0.0, d - h);
}`;

export const hallFrag = `
uniform sampler2D uA0, uB0, uA1, uB1, uA2, uB2, uA3, uB3, uA4, uB4;
uniform float uAspA[5], uAspB[5];
uniform vec3 uTint0, uTint1, uTint2, uTint3, uTint4;
uniform vec2 uS0, uS1, uS2, uS3, uS4;
uniform float uTime, uLite, uSurf, uW, uD, uH, uP, uPale, uAlpha, uHasOver;
uniform sampler2D uOver;
varying vec3 vPos;
varying float vAlong;
varying vec2 vUv;
${TAP}
${SEC}
${ZONE_FN}
void main(){
  float zs = sOf(vPos.xz);
  float th = uSurf < 0.5 ? uH * 1.05 : 6.0;
  vec3 col = vec3(0.0);
  float tot = 0.0;
  ${ZONES
    .map(
      (k) => `{
    float w = exp(-outside(zs, uS${k}) / 2.5);
    if (w > 0.02) {
      vec2 p = uSurf < 0.5 ? vec2(vAlong, vPos.y - uH * 0.5) : vec2(vPos.x, vPos.z);
      col += w * sec(uA${k}, uB${k}, uAspA[${k}], uAspB[${k}], p, th, uTint${k}, ${(k * 0.27).toFixed(2)});
      tot += w;
    }
  }`,
    )
    .join("\n  ")}
  col /= max(tot, 0.0001);
  vec2 sp = (uSurf < 0.5 ? vec2(vAlong, vPos.y) : vPos.xz) * 0.32;
  float sw = sin(sp.x * 1.7 + sin(sp.y * 2.3 + uTime * 0.5) * 1.8 + uTime * 0.45) * cos(sp.y * 1.4 - uTime * 0.33 + sin(sp.x * 1.2 + uTime * 0.2) * 1.4);
  vec3 swCol = 0.5 + 0.5 * cos(6.2831 * (vec3(0.0, 0.33, 0.67) + sp.x * 0.08 + uTime * 0.03));
  col = mix(col, vec3(1.0, 0.96, 0.88), smoothstep(0.45, 0.95, sw) * 0.2);
  col = mix(col, swCol, smoothstep(0.6, 1.0, -sw) * 0.22);
  col = 1.0 - (1.0 - col) * 0.9;
  float l = dot(col, vec3(0.299, 0.587, 0.114));
  col = clamp(mix(vec3(l), col, 3.1), 0.0, 1.0);
  float mx = max(col.r, max(col.g, col.b));
  float sat = (mx - min(col.r, min(col.g, col.b))) / max(mx, 0.001);
  col = mix(vec3(mx), col, min(1.0, 1.0 / max(sat, 0.001)));
  if (uSurf > 0.5 && uSurf < 1.5) col *= 0.93;
  if (uSurf > 1.5) col = mix(col, vec3(1.0), 0.06);
  col = mix(col, vec3(1.0), uPale);
  col = clamp((col - 0.5) * 1.06 + 0.545, 0.0, 1.0);
  if (uHasOver > 0.5) { vec4 o = texture2D(uOver, vUv); col = mix(col, o.rgb, o.a); }
  gl_FragColor = vec4(col, uAlpha);
  #include <colorspace_fragment>
}`;

// Daylight hall: warm limewash and pale floor, sun patches cast through the high openings, slow dappled leaf light, warm lamps at night.
export const dayFrag = `
uniform float uTime, uLite, uSurf, uW, uD, uH, uPale, uAlpha, uHasOver, uMotion, uDapple, uLamp;
uniform vec3 uAmb, uSunCol, uSunDir;
uniform sampler2D uOver, uPA, uPB;
uniform float uPMix, uProjOn, uProjLow;
varying vec3 vPos;
varying vec3 vN;
varying vec2 vUv;
float h21(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y);
}
float fbm(vec2 p){
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 3; i++) { s += a * vnoise(p); p = p * 2.03 + 17.1; a *= 0.5; }
  return s * 1.14;
}
float openings(float along, float hy, float d, float soft){
  float inY = smoothstep(3.95 - soft, 3.95 + soft, hy) * (1.0 - smoothstep(4.6 - soft, 4.6 + soft, hy));
  float inX = 1.0 - smoothstep(0.6 - soft, 0.6 + soft, d);
  float mull = smoothstep(0.03, 0.03 + soft * 0.6, d);
  return inY * inX * mix(0.5, 1.0, mull);
}
// traces toward the sun and checks the high openings: above the Feeling Wall (x = ±6, ±10) and both side walls (z = -4, 0, 4)
float sunThrough(vec3 P, vec3 S){
  float zb = uD * 0.5, xb = uW * 0.5, lit = 0.0;
  if (S.z < -0.02) {
    float t = (P.z + zb) / -S.z;
    float hx = P.x + S.x * t;
    float d = min(abs(abs(hx) - 6.0), abs(abs(hx) - 10.0));
    lit = max(lit, openings(hx, P.y + S.y * t, d, 0.06 + 0.035 * t));
  }
  if (abs(S.x) > 0.02) {
    float t = S.x > 0.0 ? (xb - P.x) / S.x : (P.x + xb) / -S.x;
    float hz = P.z + S.z * t;
    float d = abs(hz) < 5.4 ? abs(mod(hz + 2.0, 4.0) - 2.0) : 9.0;
    lit = max(lit, openings(hz, P.y + S.y * t, d, 0.06 + 0.035 * t));
  }
  if (P.y < uH - 0.05) {
    float t = (uH - P.y) / S.y;
    vec2 h = P.xz + S.xz * t;
    float soft = 0.06 + 0.03 * t;
    float d = abs(h.x) < 5.0 ? abs(mod(h.x + 2.0, 4.0) - 2.0) : 9.0;
    float inZ = 1.0 - smoothstep(0.55 - soft, 0.55 + soft, abs(h.y + 4.2));
    lit = max(lit, (1.0 - smoothstep(0.55 - soft, 0.55 + soft, d)) * inZ * mix(0.5, 1.0, smoothstep(0.03, 0.03 + soft * 0.6, d)));
  }
  return lit;
}
void main(){
  vec3 N = normalize(vN);
  vec2 sp = uSurf < 0.5 ? vec2(dot(vPos.xz, vec2(N.z, -N.x)), vPos.y) : vPos.xz;
  float tm = uTime * uMotion;
  vec3 base;
  if (uSurf < 0.5) {
    float stroke = vnoise(vec2(sp.x * 2.2 + vPos.y * 0.6, vPos.y * 9.0));
    // richer warm pastel limewash (apricot-rose, drifting to lilac), not pale plaster
    base = vec3(0.93, 0.79, 0.69) * (0.95 + 0.08 * fbm(sp * 1.3));
    base *= mix(vec3(1.0), mix(vec3(1.0, 0.9, 0.88), vec3(0.9, 0.86, 1.0), vnoise(sp * 0.35)), 0.7) * (0.975 + 0.05 * stroke);
  }
  else if (uSurf < 1.5) {
    float f = fract(vPos.x * 0.7);
    float seam = 1.0 - smoothstep(0.006, 0.02, min(f, 1.0 - f));
    base = vec3(0.76, 0.6, 0.47) * (0.9 + 0.14 * fbm(vPos.xz * vec2(0.5, 2.6))) * (1.0 - 0.1 * seam);
    base *= mix(vec3(1.0), mix(vec3(1.0, 0.94, 0.92), vec3(0.95, 0.97, 1.0), vnoise(vPos.xz * 0.25 + 3.0)), 0.6);
  } else base = vec3(0.89, 0.77, 0.73) * (0.94 + 0.07 * fbm(sp * 0.8));
  // real shadows: soft occlusion where walls meet the floor, the ceiling and each other
  float ex = 0.5 * uW - abs(vPos.x), ez = 0.5 * uD - abs(vPos.z);
  float ao = 1.0;
  if (uSurf < 0.5) ao = mix(0.68, 1.0, smoothstep(0.0, 1.3, vPos.y)) * mix(0.8, 1.0, smoothstep(0.0, 0.9, uH - vPos.y)) * mix(0.78, 1.0, smoothstep(0.0, 1.6, abs(N.x) > 0.5 ? ez : ex));
  else ao = mix(uSurf < 1.5 ? 0.66 : 0.78, 1.0, smoothstep(0.0, 1.8, min(ex, ez)));
  base *= ao;
  vec3 light = uAmb;
  if (uSurf < 0.5) light *= 0.93 + 0.09 * smoothstep(0.0, uH, vPos.y);
  vec3 S = normalize(uSunDir);
  float sun = 0.0;
  float zb = uD * 0.5;
  if (vPos.z < zb - 0.04 && S.y > 0.02) {
    float lam = uSurf < 0.5 ? max(dot(N, S), 0.0) : (uSurf < 1.5 ? S.y : 0.0);
    if (lam > 0.0) sun = sunThrough(vPos, S) * lam;
  }
  vec2 dp = sp * 1.5 + vec2(tm * 0.03, -tm * 0.02);
  float leaves = fbm(dp + 0.8 * vec2(vnoise(dp * 0.7 + tm * 0.01), vnoise(dp * 0.7 - 3.1)));
  float dap = smoothstep(0.38, 0.62, leaves);
  sun *= mix(1.0, 0.3 + 0.95 * dap, uDapple);
  light *= mix(1.0, 0.94 + 0.08 * dap, uDapple);
  vec3 col = base * (light + uSunCol * sun * 1.7);
  if (uLamp > 0.01 && uSurf < 0.5 && vPos.z < zb + 0.1) {
    float cx = mod(sp.x + 2.0, 4.0) - 2.0;
    float g = exp(-(cx * cx * 0.9 + pow(vPos.y - 3.0, 2.0) * 0.6));
    col += vec3(1.0, 0.7, 0.4) * g * 0.3 * uLamp;
  }
  if (uLamp > 0.01 && uSurf > 0.5 && uSurf < 1.5) col += vec3(1.0, 0.74, 0.46) * 0.07 * uLamp;
  col -= max(col - 0.9, 0.0) * 0.6;
  // flowing brushstroke projections of Priyanka's paintings: upper side/south walls and ceiling only, lightened so they never darken
  // never on the Feeling Wall (arches, labels, heading); side and south walls down to the skirting, the ceiling, and the floor
  float proj = 0.0;
  if (uSurf > 1.5) proj = 1.0;
  else if (uSurf > 0.5) proj = 0.62 * smoothstep(0.6, 2.6, vPos.z + zb) * smoothstep(0.0, 1.2, 0.5 * uW - abs(vPos.x)) * smoothstep(0.0, 1.0, zb - vPos.z);
  else if (N.z < 0.5) {
    proj = smoothstep(0.25, 1.0, vPos.y) * mix(0.55, 1.0, smoothstep(1.0, 3.6, vPos.y));
    if (N.z < -0.5) {
      // mask around the Memory Wall opening and the three Living Windows (with their frames)
      float alc = step(abs(vPos.x), 2.95) * step(vPos.y, 3.85);
      float wx = min(min(abs(vPos.x - 4.6), abs(vPos.x - 6.8)), abs(vPos.x - 9.0));
      float win = (1.0 - smoothstep(0.95, 1.25, wx)) * (1.0 - smoothstep(3.75, 4.05, vPos.y)) * smoothstep(0.35, 0.6, vPos.y);
      proj *= (1.0 - alc) * (1.0 - win);
    }
  }
  if (uProjLow < 0.5 && uSurf < 1.5 && !(uSurf < 0.5 && vPos.y > (N.z < -0.5 ? 4.0 : 3.0))) proj = 0.0;
  if (vPos.z > zb - 0.02) proj = 0.0;
  proj *= uProjOn;
  if (proj > 0.01) {
    vec2 q = uSurf > 1.5 ? vPos.xz * vec2(0.075, 0.1) : (uSurf > 0.5 ? vPos.xz * vec2(0.068, 0.09) + vec2(0.37, 0.21) : vec2(sp.x * 0.075, vPos.y * 0.13));
    // the floor (a large share of the screen) takes a lighter path: one gentle warp, no brush strokes or swirl
    bool fl = uSurf > 0.5 && uSurf < 1.5;
    vec2 w = fl ? vec2(sin(q.y * 7.0 + tm * 0.11), 0.0) * 0.05 : vec2(sin(q.y * 7.0 + q.x * 3.0 + tm * 0.11), sin(q.x * 6.0 - q.y * 2.0 - tm * 0.09)) * 0.06;
    vec2 uv = q + vec2(tm * 0.006, tm * 0.0025) + w;
    float stroke = fl ? 0.5 : 0.5 + 0.5 * sin(dot(uv, vec2(0.8, 0.6)) * 90.0 + w.y * 40.0);
    vec2 uvm = abs(fract(uv * 0.5) * 2.0 - 1.0);
    // the floor reads a small mip level: soft painted light, and cheap for phones
    float lod = fl ? 1.5 : 0.0;
    vec3 pc = texture2D(uPA, uvm, lod).rgb;
    if (uPMix > 0.001) pc = mix(pc, texture2D(uPB, uvm, lod).rgb, uPMix);
    vec3 tint = uAmb / max(max(uAmb.r, uAmb.g), uAmb.b);
    pc = mix(vec3(dot(pc, vec3(0.3, 0.59, 0.11))), pc, 1.85);
    // clean and luminous, never muddy: lift the darks a little, keep the colour
    pc = (max(pc, vec3(0.0)) * (fl ? 0.97 : 0.88) + (fl ? 0.04 : 0.14)) * mix(vec3(1.0), tint, 0.35);
    float swirl = fl ? 0.6 : smoothstep(-0.5, 0.9, sin(uv.x * 9.0 + uv.y * 5.0 + tm * 0.05 + w.x * 20.0));
    float a = proj * (0.58 + 0.27 * stroke) * (0.6 + 0.4 * swirl);
    // on the floor the painting glows into the wood like light through coloured glass, so it stays rich, never milky
    col = fl ? mix(col, clamp(pc * col * 1.3, 0.0, 1.0), min(1.0, a * 1.05)) : mix(col, pc * 1.04, a);
  }
  // richer gallery tone: a little more contrast and colour, no milky wash
  col = clamp((col - 0.55) * 1.14 + 0.54, 0.0, 1.0);
  col = clamp(mix(vec3(dot(col, vec3(0.299, 0.587, 0.114))), col, 1.15), 0.0, 1.0);
  col = mix(col, vec3(1.0), uPale);
  if (uHasOver > 0.5) { vec4 o = texture2D(uOver, vUv); col = mix(col, o.rgb, o.a); }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), uAlpha);
  #include <colorspace_fragment>
}`;

export const doorVert = `
varying vec2 vUv;
void main(){
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

export const doorFrag = `
uniform sampler2D uMap;
uniform float uAspect, uHot, uTime, uW, uH, uPadX, uPadY, uNear, uVisited, uPhase;
uniform vec3 uGlow, uTone;
varying vec2 vUv;
void main(){
  vec2 p = (vUv - vec2(0.5, 0.0)) * vec2(uW + uPadX, uH + uPadY);
  float r = uW * 0.5;
  float cy = uH - r;
  float d = p.y < cy ? abs(p.x) - r : length(vec2(p.x, p.y - cy)) - r;
  if (d < 0.0) {
    vec2 q = vec2(p.x / uW + 0.5, p.y / uH);
    float boxA = uW / uH;
    vec2 uv = q;
    if (uAspect > boxA) uv.x = 0.5 + (q.x - 0.5) * boxA / uAspect;
    else uv.y = 0.5 + (q.y - 0.5) * uAspect / boxA;
    vec3 c = texture2D(uMap, uv, mix(2.5, 0.0, uHot)).rgb;
    float spot = mix(0.92, 1.05, smoothstep(0.1, 0.95, q.y)) * (1.0 - 0.14 * pow(length((q - 0.5) * vec2(1.0, 0.8)) * 1.35, 2.2));
    c = c * spot + vec3(1.0, 0.97, 0.9) * 0.05 * smoothstep(0.5, 1.0, q.y);
    c = mix(c, vec3(1.0), 0.03 + 0.07 * (1.0 - uHot));
    c = mix(c, vec3(1.0), smoothstep(-0.1, 0.0, d) * 0.85);
    gl_FragColor = vec4(c, 1.0);
  } else {
    float breath = 0.5 + 0.5 * sin(uTime * 1.795 + uPhase);
    float base = mix(0.4 + 0.24 * breath, 0.3 + 0.06 * breath, uVisited);
    float lift = uNear * mix(0.38, 0.16, uVisited) + uHot * 0.45;
    float g = exp(-d * mix(5.2, 4.0, uNear)) * (base + lift);
    float along = p.y < cy ? (p.x > 0.0 ? p.y : cy + 3.14159 * r + (cy - p.y)) : cy + atan(p.y - cy, p.x) * r;
    float sh = pow(0.5 + 0.5 * sin(along * 3.0 - uTime * 1.4), 8.0) * exp(-d * 14.0) * (uNear * 0.5 + uHot * 0.7) * (1.0 - 0.5 * uVisited);
    if (g + sh < 0.01) discard;
    vec3 col = mix(uTone, vec3(1.0), mix(0.3, 0.55, uVisited));
    col = mix(col, vec3(1.0, 0.86, 0.66), uNear * 0.3 + uHot * 0.2);
    col = mix(col, vec3(1.0, 0.95, 0.85), clamp(sh * 2.0, 0.0, 1.0));
    gl_FragColor = vec4(col, min(1.0, g + sh));
  }
  #include <colorspace_fragment>
}`;

export const plainVert = `
varying vec2 vUv;
void main(){
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

export const roomFrag = `
uniform sampler2D uA, uB;
uniform float uAspA, uAspB, uTime, uLite, uSurf, uH, uUvLen, uDim, uPetal, uHaze, uPale, uVivid, uLift, uGamma;
uniform vec3 uTint, uLight, uShade, uHazeCol;
varying vec3 vPos;
varying float vAlong;
varying vec2 vUv;
${TAP}
${SEC}
float petal(vec2 p){
  vec2 q = p * 0.9 + vec2(uTime * 0.22, -uTime * 0.3);
  vec2 id = floor(q);
  vec2 f = fract(q) - 0.5;
  float h = fract(sin(dot(id, vec2(12.9898, 78.233))) * 43758.5453);
  if (h < 0.5) return 0.0;
  f += vec2(sin(uTime * 0.7 + h * 9.0), cos(uTime * 0.5 + h * 7.0)) * 0.18;
  float a = h * 6.28 + uTime * (0.5 + h);
  vec2 r = mat2(cos(a), -sin(a), sin(a), cos(a)) * f;
  return smoothstep(0.13, 0.08, length(r * vec2(1.0, 2.4)));
}
void main(){
  float along = uUvLen > 0.0 ? vUv.x * uUvLen : vAlong;
  vec2 p = uSurf < 0.5 ? vec2(along, vPos.y - uH * 0.5) : vPos.xz;
  float th = uSurf < 0.5 ? uH * 1.05 : 6.0;
  vec3 col = sec(uA, uB, uAspA, uAspB, p, th, uTint, 0.31);
  float l = dot(col, vec3(0.299, 0.587, 0.114));
  col = clamp(mix(vec3(l), col, 1.9), 0.0, 1.0);
  float sw = sin(p.x * 0.55 + sin(p.y * 0.7 + uTime * 0.5) * 1.6 + uTime * 0.4) * cos(p.y * 0.45 - uTime * 0.3);
  col += smoothstep(0.5, 1.0, sw) * 0.14;
  col *= uLight;
  col = mix(col, uShade, uDim);
  if (uSurf > 0.5 && uSurf < 1.5) col *= 0.82;
  if (uSurf > 1.5) col *= 0.78;
  if (uPetal > 0.5 && uSurf < 0.5) col = mix(col, vec3(1.0, 0.8, 0.88), petal(p) * 0.85);
  if (uHaze > 0.0) col = mix(col, uHazeCol, uHaze * (uSurf > 1.5 ? 0.6 : smoothstep(3.5, 0.0, vPos.y) * 0.8 + 0.15));
  col = mix(col, vec3(1.0), uPale);
  { float l = dot(col, vec3(0.299, 0.587, 0.114)); col = pow(clamp(col, 0.0, 1.0), vec3(uGamma)); l = dot(col, vec3(0.299, 0.587, 0.114)); col = clamp(mix(vec3(l), col, uVivid) * uLift * 1.04, 0.0, 1.0); }
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}`;

export const glowFrag = `
uniform vec3 uColor;
uniform float uNear, uTime, uPad;
uniform vec2 uBox;
varying vec2 vUv;
void main(){
  vec2 p = (vUv - 0.5) * (uBox + vec2(uPad * 2.0));
  vec2 q = abs(p) - uBox * 0.5;
  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
  if (d < 0.0) discard;
  float g = exp(-d * 7.0) * (0.16 + 0.1 * (0.5 + 0.5 * sin(uTime * 1.8)) + uNear * 0.6);
  gl_FragColor = vec4(uColor * g, 1.0);
}`;

export const wispFrag = `
uniform float uTime, uSeed;
varying vec2 vUv;
void main(){
  float y = vUv.y;
  float x = 0.5 + 0.2 * sin(y * 7.0 - uTime * 0.9 + uSeed) * y + 0.06 * sin(y * 17.0 - uTime * 1.7 + uSeed * 2.0) * y;
  float w = 0.012 + 0.035 * y;
  float a = smoothstep(w, 0.0, abs(vUv.x - x)) * smoothstep(0.0, 0.15, y) * (1.0 - smoothstep(0.55, 1.0, y)) * 0.6;
  gl_FragColor = vec4(vec3(1.0, 0.93, 0.85) * a, 1.0);
}`;

export const shaftFrag = `
uniform float uTime;
varying vec2 vUv;
void main(){
  float c = 0.5 + (vUv.y - 0.5) * 0.35;
  float a = smoothstep(0.3, 0.0, abs(vUv.x - c)) * smoothstep(0.0, 0.4, vUv.y) * (0.65 + 0.35 * sin(uTime * 0.4 + vUv.y * 3.0));
  gl_FragColor = vec4(vec3(0.78, 0.86, 1.0) * a * 0.3, 1.0);
}`;

export const flameFrag = `
uniform float uTime, uSeed;
varying vec2 vUv;
void main(){
  vec2 p = (vUv - vec2(0.5, 0.3)) * vec2(2.0, 1.0);
  float d = length(p * vec2(1.0, 0.55));
  float f = 0.85 + 0.15 * sin(uTime * 13.0 + uSeed) + 0.1 * sin(uTime * 7.3 + uSeed * 2.0);
  float a = exp(-d * 6.0) * f;
  gl_FragColor = vec4(vec3(1.0, 0.72, 0.3) * a, 1.0);
}`;
